<script setup>
    import { computed } from 'vue';

    const props = defineProps({
        selectedSort: {
            type: String,
            default: 'POPULARITY',
        },
        // Enhedens navn, fx "TB". Er den ikke sat, har kategorien ingen mængde
        // at dividere prisen med, og valget ville sortere på ingenting.
        unitPriceLabel: {
            type: String,
            default: null,
        },
    });

    const emit = defineEmits(['sortingUpdated']);

    // Popularitet står først og er standard. Var det "Nyeste", altså det vi
    // importerede sidst, og det er den mest tilfældige liste vi kan give en
    // førstegangsbesøgende.
    const baseOptions = [
        { key: 'POPULARITY', value: 'Popularitet' },
        { key: 'PRICE_LOW_HIGH', value: 'Pris, lav til høj' },
        { key: 'PRICE_HIGH_LOW', value: 'Pris, høj til lav' },
        { key: 'NEWEST', value: 'Nyeste' },
        { key: 'OLDEST', value: 'Ældste' },
        { key: 'NAME_A_Z', value: 'Navn, A til Å' },
        { key: 'NAME_Z_A', value: 'Navn, Å til A' },
    ];

    // "Den største SSD til de mindste penge" er det opslag edbpriser-brugerne
    // savner mest, og det er én sortering når prisen pr. enhed er regnet ud.
    const sortOptions = computed(() => {
        if (!props.unitPriceLabel) return baseOptions;

        return [
            baseOptions[0],
            { key: 'UNIT_PRICE', value: `Pris pr. ${props.unitPriceLabel}, lav til høj` },
            ...baseOptions.slice(1),
        ];
    });

    // Afledt af proppen, ikke låst ved opsætning. Den gamle udgave slog kun op
    // én gang, så knappen holdt fast i den første sortering, og en ukendt
    // nøgle i URL'en fik hele siden til at vælte på `undefined.value`.
    const currentSort = computed(
        () => sortOptions.value.find(o => o.key === props.selectedSort) || sortOptions.value[0],
    );

    const changeSort = (key) => {
        if (key === currentSort.value.key) return;
        emit('sortingUpdated', key);
    };
</script>

<template>
    <div class="sorting-widget">
        <label class="sorting-widget__label" for="sorteringsvalg">Sortér efter</label>
        <div class="dropdown">
            <button
                id="sorteringsvalg"
                class="btn btn-sorting dropdown-toggle"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
            >
                {{ currentSort.value }}
            </button>
            <ul class="dropdown-menu" aria-labelledby="sorteringsvalg">
                <li v-for="option in sortOptions" :key="option.key">
                    <button
                        type="button"
                        class="dropdown-item"
                        :class="{ active: option.key === currentSort.key }"
                        @click="changeSort(option.key)"
                    >
                        {{ option.value }}
                    </button>
                </li>
            </ul>
        </div>
    </div>
</template>

<style scoped>
    .sorting-widget {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .sorting-widget__label {
        font-size: var(--pc-text-sm);
        color: var(--pc-text-muted);
        white-space: nowrap;
        margin: 0;
    }

    .dropdown {
        display: inline-block;
    }

    .btn-sorting {
        background-color: var(--pc-bg-tertiary);
        color: var(--pc-text-primary);
        border: 1px solid var(--pc-border);
        font-size: var(--pc-text-sm);
        font-weight: 500;
        padding: 0.35rem 0.75rem;
    }

    .btn-sorting:hover,
    .btn-sorting:focus {
        background-color: var(--pc-bg-hover);
        color: var(--pc-text-primary);
        border-color: var(--pc-border-hover);
    }

    .dropdown-menu {
        background-color: var(--pc-bg-secondary);
        border: 1px solid var(--pc-border);
    }

    .dropdown-item {
        color: var(--pc-text-primary);
        background: transparent;
        cursor: pointer;
        width: 100%;
        text-align: left;
    }

    .dropdown-item:hover,
    .dropdown-item:focus {
        background-color: var(--pc-bg-hover);
        color: var(--pc-text-primary);
    }

    .dropdown-item.active {
        background-color: var(--pc-accent);
        color: var(--pc-text-primary);
    }

    @media (max-width: 575.98px) {
        .sorting-widget__label {
            display: none;
        }
    }
</style>
